/**
 * Offer Type Definitions for Frontend
 *
 * These types define the structure of NFT offer data from XRPL and database,
 * including buy offers, sell offers, and cafe-brokered offers.
 */

import { isDealsBrokeredOffer as isDealsBrokered } from '../lib/marketplace-constants';

/**
 * Offer type enum
 */
export type OfferType = 'buy' | 'sell';

/**
 * Offer status enum
 */
export type OfferStatus =
  | 'pending'
  | 'active'
  | 'accepted'
  | 'declined'
  | 'expired'
  | 'cancelled';

/**
 * Data source for offers
 */
export type OfferSource = 'xrpl' | 'database' | 'unavailable';

/**
 * XRPL Offer from Clio server (raw format)
 */
export interface XRPLOffer {
  amount: string | XRPLAmount;
  flags: number;
  nft_offer_index: string;
  owner: string;
  destination?: string;
  expiration?: number;
}

/**
 * XRPL Amount object (for IOUs)
 */
export interface XRPLAmount {
  currency: string;
  issuer: string;
  value: string;
}

/**
 * Formatted offer for display (from XRPL)
 */
export interface FormattedOffer {
  amount: number; // XRP drops
  creator: string; // XRPL address
  offerIndex: string; // XRPL offer index
  destination?: string; // Destination address (for sell offers)
  expiresAt?: number; // Unix timestamp
  isMarketplaceBrokered?: boolean; // True if destination is any supported marketplace broker
}

/**
 * Database offer (from our offers table)
 */
export interface DatabaseOffer {
  id: string;
  nft_token_id: string;
  creator_address: string;
  recipient_address?: string;
  offer_type: OfferType;
  amount: number;
  currency: string;
  status: OfferStatus;
  xrpl_offer_index?: string;
  expires_at?: string;
  accepted_by?: string;
  accepted_at?: string;
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

/**
 * NFT Offers API Response
 */
export interface NFTOffersResponse {
  success: boolean;
  data: {
    buyOffers: FormattedOffer[];
    sellOffers: FormattedOffer[];
    source: OfferSource;
  };
  error?: string;
}

/**
 * Create Offer Request
 */
export interface CreateOfferRequest {
  nftTokenId: string;
  offerType: OfferType;
  amount: number; // XRP drops
  expiresIn?: number; // Seconds until expiration
  destination?: string; // For transfers (amount=0)
}

/**
 * Xaman Payload (from Xaman SDK)
 */
export interface XamanPayload {
  uuid: string;
  next: {
    always: string;
    no_push_msg_received: string;
  };
  refs: {
    qr_png: string;
    qr_matrix: string;
    qr_uri_quality_opts: string[];
    websocket_status: string;
  };
  pushed: boolean;
}

/**
 * Create Offer API Response
 */
export interface CreateOfferResponse {
  success: boolean;
  data: {
    payload: XamanPayload;
    offerId?: string; // Our database offer ID
  };
  error?: string;
}

/**
 * Accept Offer Request
 */
export interface AcceptOfferRequest {
  offerIndex: string; // XRPL offer index
  nftTokenId: string;
}

/**
 * Accept Offer API Response
 */
export interface AcceptOfferResponse {
  success: boolean;
  data: {
    payload: XamanPayload;
  };
  error?: string;
}

/**
 * Offers Update Event (from SSE)
 */
export interface OffersUpdateEvent {
  type: 'offers-update';
  data: {
    nftTokenId: string;
    buyOffers: FormattedOffer[];
    sellOffers: FormattedOffer[];
    timestamp: number;
  };
}

/**
 * Cafe Fee Breakdown for display
 */
export interface CafeFeeBreakdown {
  sellAmount: number; // XRP drops
  cafeFee: number; // XRP drops
  totalAmount: number; // XRP drops
  sellAmountXRP: string; // Formatted string
  cafeFeeXRP: string; // Formatted string
  totalAmountXRP: string; // Formatted string
}

/**
 * Check if offer is expired
 */
export function isOfferExpired(offer: FormattedOffer): boolean {
  if (!offer.expiresAt) return false;
  return Date.now() > offer.expiresAt * 1000;
}

/**
 * Check if offer is marketplace-brokered (any supported marketplace)
 */
export function isMarketplaceBrokeredOffer(offer: FormattedOffer): boolean {
  return offer.isMarketplaceBrokered === true;
}

/**
 * @deprecated Use isMarketplaceBrokeredOffer instead
 */
export function isCafeBrokeredOffer(offer: FormattedOffer): boolean {
  return isMarketplaceBrokeredOffer(offer);
}

/**
 * Sort buy offers (highest first)
 */
export function sortBuyOffers(offers: FormattedOffer[]): FormattedOffer[] {
  return [...offers].sort((a, b) => b.amount - a.amount);
}

/**
 * Sort sell offers (lowest first, xrp.deals preferred on price tie)
 */
export function sortSellOffers(offers: FormattedOffer[]): FormattedOffer[] {
  return [...offers].sort((a, b) => {
    if (a.amount !== b.amount) return a.amount - b.amount;
    // Prefer xrp.deals offers at same price
    const aIsDeals = isDealsBrokered(a.destination);
    const bIsDeals = isDealsBrokered(b.destination);
    if (aIsDeals && !bIsDeals) return -1;
    if (!aIsDeals && bIsDeals) return 1;
    return 0;
  });
}

/**
 * Get lowest marketplace-brokered sell offer (current buy price)
 */
export function getLowestMarketplaceBrokeredSellOffer(offers: FormattedOffer[]): FormattedOffer | null {
  const marketplaceOffers = offers.filter(offer => isMarketplaceBrokeredOffer(offer) && !isOfferExpired(offer));
  const sorted = sortSellOffers(marketplaceOffers);
  return sorted[0] || null;
}

/**
 * @deprecated Use getLowestMarketplaceBrokeredSellOffer instead
 */
export function getLowestCafeSellOffer(offers: FormattedOffer[]): FormattedOffer | null {
  return getLowestMarketplaceBrokeredSellOffer(offers);
}

/**
 * Get lowest sell offer (any type - P2P or marketplace-brokered)
 */
export function getLowestSellOffer(offers: FormattedOffer[]): FormattedOffer | null {
  const validOffers = offers.filter(offer => !isOfferExpired(offer));
  const sorted = sortSellOffers(validOffers);
  return sorted[0] || null;
}

/**
 * Convert XRP drops to XRP
 */
export function dropsToXRP(drops: number): number {
  return drops / 1_000_000;
}

/**
 * Format XRP amount for display
 */
export function formatOfferAmount(drops: number, decimals: number = 2): string {
  const xrp = dropsToXRP(drops);
  return `${xrp.toFixed(decimals)} XRP`;
}

/**
 * Format expiration date for display
 */
export function formatExpirationDate(expiresAt: number | undefined): string {
  if (!expiresAt) return 'No expiration';
  const date = new Date(expiresAt * 1000);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
